import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScanLine, ClipboardCheck, LogIn, Wrench, Building2, Loader2, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useShiftStore } from '../../store/useShiftStore';

export default function GateHome() {
  const user = useAuthStore((s) => s.user);
  const facilities = useShiftStore((s) => s.facilities);
  const status = useShiftStore((s) => s.status);
  const current = useShiftStore((s) => s.facilities.find((f) => f.public_id === s.facilityId));
  const load = useShiftStore((s) => s.load);
  const error = useShiftStore((s) => s.error);

  useEffect(() => { if (status === 'idle') load(); }, [status, load]);

  if (status === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }

  if (status === 'error') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <Building2 className="mx-auto h-8 w-8 text-rose-400" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-900">Couldn&apos;t load your facilities</p>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button type="button" onClick={load} className="btn-ghost mx-auto mt-4">Try again</button>
      </div>
    );
  }

  if (facilities.length === 0) {
    // An owner is unscoped — telling them to ask an administrator for access
    // they already have is nonsense. The real state is that no facility exists.
    const isOwner = user?.role === 'super_admin';
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <Building2 className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-900">
          {isOwner ? 'No facilities yet' : 'No facility assigned'}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {isOwner
            ? 'Add a facility and its units, then this console becomes usable.'
            : 'Ask an administrator to add you to a facility before your shift.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <h1 className="text-xl font-extrabold tracking-tight text-slate-900">On shift</h1>
      <p className="mt-1 text-sm text-slate-600">
        {user?.first_name}, you&apos;re working {current?.name ?? 'this facility'}.
      </p>

      {current && (
        <dl className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="Occupancy" value={`${current.occupancy_pct}%`} hint={`${current.occupied_units}/${current.total_units} units`} />
          <Stat label="Open issues" value={current.open_tickets} hint="maintenance queue"
                warn={current.open_tickets > 0} />
          <Stat label="Access" value={current.is_24h_access ? '24h' : 'Gated'} hint={current.is_24h_access ? 'always open' : 'set hours'} />
        </dl>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Action to="/gate/verify" Icon={ScanLine} label="Verify entry"
                hint="Check a gate code and log the event" />
        <Action to="/gate/arrivals" Icon={LogIn} label="Move-in / move-out"
                hint="Admit arrivals, release spaces" />
        <Action to="/gate/audits" Icon={ClipboardCheck} label="Lot audit"
                hint="Walk the yard, confirm each space" />
        <Action to="/gate/tickets" Icon={Wrench} label="Report an issue"
                hint="Damage, lighting, fencing, gate faults" />
      </div>
    </div>
  );
}

function Stat({ label, value, hint, warn }) {
  return (
    <div className={`rounded-xl border p-3 ${warn ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <dt className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 flex items-center gap-1.5 text-2xl font-extrabold text-slate-900">
        {warn && <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />}
        {value}
      </dd>
      <p className="text-[12px] text-slate-400">{hint}</p>
    </div>
  );
}

/** Large targets: this screen is used one-handed, outdoors, on a tablet. */
function Action({ to, Icon, label, hint }) {
  return (
    <Link to={to} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5 transition hover:border-brand-400 hover:shadow-card">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span>
        <span className="block text-sm font-bold text-slate-900">{label}</span>
        <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>
      </span>
    </Link>
  );
}
